/* =========================================================================
   Settings → Legal pages: Terms of Use / Privacy Policy / Cookie Policy.

   All three load from GET /config/admin/legal; each saves on its own with
   PUT /config/admin/legal/:type (the backend sanitizes the HTML, bumps the
   version when the content/URL changes, and answers 422 + a per-field
   `errors` map). Each document is either written here (HTML editor with a
   live preview -- the project has no rich-text editor) or links to an
   external https page.

   RBAC: settings.view to read (the backend gates the GET), settings.edit to
   change anything -- without it every input is disabled and there is no
   Save button.
   ========================================================================= */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdOpenInNew } from "react-icons/md";
import styles from "./Settings.module.css";
import { getLegalDocuments, previewLegalHtml, saveLegalDocument } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import { useTenant } from "../../context/TenantContext";
import useUnsavedChangesGuard from "./useUnsavedChangesGuard";
import { isHttpsUrl } from "./appInfoFields";
import {
  EDITOR_TOOLS,
  LEGAL_MODES,
  LEGAL_SECTIONS,
  TITLE_MAX,
  applyEditorTool,
  firstMessage,
  formatUpdatedAt,
  isLegalDirty,
  legalFormFrom,
  legalPreviewUrl,
  toLegalPayload,
  validateLegalForm,
} from "./legalFields";

// Origin of the public website, for "Preview" in content mode. One AdminPanel
// bundle serves every tenant, so the env var can only ever name one site —
// prefer the tenant's own viewer domain (TenantDomain with isPrimary, which is
// the viewer host; the admin host is kind: "admin") and keep the env var as a
// single-tenant/legacy fallback. Optional: when neither resolves, Preview is
// only available for external URLs (the live preview below the editor still
// works).
const WEBSITE_URL_ENV = process.env.REACT_APP_WEBSITE_URL || "";

const tenantWebsiteOrigin = (tenant) => {
  const d = tenant?.primaryDomain || tenant?.domains?.find((x) => x.isPrimary)?.domain;
  if (!d) return "";
  return /^https?:\/\//i.test(d) ? d : `https://${d}`;
};

// The preview renders the HTML in a sandboxed iframe (no scripts, no
// same-origin) so pasted markup can never run in the CMS.
const previewDoc = (html) =>
  `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;line-height:1.55;color:#222;margin:12px;}
    table{border-collapse:collapse;}th,td{border:1px solid #ccc;padding:4px 8px;}
  </style></head><body>${html || '<p style="color:#888">Nothing written yet.</p>'}</body></html>`;

const byKey = (docs) => Object.fromEntries((docs || []).map((d) => [d.type, d]));

// `sectionKey`: which document to show; null keeps the forms (and their
// unsaved edits) mounted while another sub-menu item is showing.
const LegalSettings = ({ sectionKey, onDirtyChange }) => {
  const { tenant } = useTenant();
  // Tenant's own site first; the build-time env var only as a fallback.
  const websiteUrl = tenantWebsiteOrigin(tenant) || WEBSITE_URL_ENV;
  const { can } = useRbac();
  const canEdit = can("settings", "edit");

  const [docs, setDocs] = useState(null); // { type: adminDoc } as last saved
  const [loadError, setLoadError] = useState("");
  const [values, setValues] = useState({}); // { type: form }
  const [errors, setErrors] = useState({}); // { type: { field: msg } }
  const [saving, setSaving] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getLegalDocuments()
      .then((res) => {
        if (cancelled) return;
        const map = byKey(res?.data);
        setDocs(map);
        setValues(Object.fromEntries(LEGAL_SECTIONS.map((s) => [s.key, legalFormFrom(map[s.key], s)])));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || "Could not load the legal pages.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const savedForms = useMemo(
    () => (docs ? Object.fromEntries(LEGAL_SECTIONS.map((s) => [s.key, legalFormFrom(docs[s.key], s)])) : {}),
    [docs]
  );
  const dirtyKeys = docs && canEdit ? LEGAL_SECTIONS.filter((s) => isLegalDirty(values[s.key], savedForms[s.key])).map((s) => s.key) : [];
  const dirtyStr = dirtyKeys.join(",");

  useUnsavedChangesGuard(dirtyKeys.length > 0);
  useEffect(() => {
    onDirtyChange?.(dirtyStr ? dirtyStr.split(",") : []);
  }, [dirtyStr, onDirtyChange]);

  const setField = useCallback((type, name, value) => {
    setValues((prev) => ({ ...prev, [type]: { ...prev[type], [name]: value } }));
    setErrors((prev) => {
      if (!prev[type]?.[name]) return prev;
      const next = { ...prev[type] };
      delete next[name];
      return { ...prev, [type]: next };
    });
  }, []);

  // The live preview shows the HTML as the backend will store it (sanitized),
  // not the raw textarea, so what you see is what gets saved. Debounced; until
  // the first answer (or if it fails) the preview falls back to the draft.
  const [previews, setPreviews] = useState({}); // { type: sanitized html }
  const draftHtml = sectionKey ? values[sectionKey]?.content_html : undefined;
  useEffect(() => {
    if (draftHtml === undefined) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      previewLegalHtml(draftHtml)
        .then((res) => {
          if (!cancelled) setPreviews((prev) => ({ ...prev, [sectionKey]: res?.data?.content_html ?? "" }));
        })
        .catch(() => {});
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sectionKey, draftHtml]);

  const section = LEGAL_SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return null;

  if (loadError) {
    return (
      <section className={styles.intPanel} aria-label="Legal pages">
        <h3 className={styles.sectionHeading}>{section.title}</h3>
        <p role="alert" className={styles.errorText}>{loadError}</p>
      </section>
    );
  }
  if (!docs) {
    return (
      <section className={styles.intPanel} aria-label={section.title}>
        <h3 className={styles.sectionHeading}>{section.title}</h3>
        <p className={styles.sectionIntro}>Loading…</p>
      </section>
    );
  }

  const type = section.key;
  const form = values[type];
  const fieldErrors = errors[type] || {};
  const saved = docs[type];
  const dirty = dirtyKeys.includes(type);
  const isUrlMode = form.mode === LEGAL_MODES.URL;
  const previewUrl = legalPreviewUrl(section, form, websiteUrl);
  const id = (name) => `legal-${type}-${name}`;

  const handleSave = async () => {
    const clientErrors = validateLegalForm(form);
    if (Object.keys(clientErrors).length) {
      setErrors((prev) => ({ ...prev, [type]: clientErrors }));
      showToast("Please fix the highlighted fields.", "error");
      return;
    }
    setSaving(type);
    try {
      const res = await saveLegalDocument(type, toLegalPayload(form));
      const doc = res?.data;
      if (doc) {
        setDocs((prev) => ({ ...prev, [type]: doc }));
        setValues((prev) => ({ ...prev, [type]: legalFormFrom(doc, section) }));
      }
      setErrors((prev) => ({ ...prev, [type]: {} }));
      showToast(`${section.title} saved.`, "success");
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 422 && data?.errors) {
        setErrors((prev) => ({
          ...prev,
          [type]: Object.fromEntries(Object.entries(data.errors).map(([k, v]) => [k, firstMessage(v)])),
        }));
        showToast(data.message || "Some fields are invalid.", "error");
      } else {
        showToast(data?.message || `Could not save ${section.title}.`, "error");
      }
    } finally {
      setSaving(null);
    }
  };

  const handleDiscard = () => {
    setValues((prev) => ({ ...prev, [type]: savedForms[type] }));
    setErrors((prev) => ({ ...prev, [type]: {} }));
  };

  const applyTool = (tool) => {
    const el = editorRef.current;
    const text = form.content_html || "";
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const next = applyEditorTool(text, start, end, tool);
    setField(type, "content_html", next.text);
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      editorRef.current.setSelectionRange(next.start, next.end);
    });
  };

  const errorFor = (name) =>
    fieldErrors[name] ? (
      <span id={`${id(name)}-error`} role="alert" className={styles.errorText}>
        {fieldErrors[name]}
      </span>
    ) : null;

  const inputProps = (name) => ({
    id: id(name),
    name,
    disabled: !canEdit,
    readOnly: !canEdit,
    "aria-invalid": Boolean(fieldErrors[name]),
    "aria-describedby": fieldErrors[name] ? `${id(name)}-error` : undefined,
  });

  const updatedAt = formatUpdatedAt(saved?.updated_at);
  const trimmedUrl = String(form.external_url || "").trim();

  return (
    <section className={styles.intPanel} aria-label={section.title}>
      <header className={styles.intHeader}>
        <div>
          <h3 className={styles.sectionHeading}>{section.title}</h3>
          <p className={styles.sectionIntro}>
            Shown on the website at <code>{section.path}</code> when written here, or linked to an external page.
          </p>
        </div>
        <div className={styles.intHeaderActions} data-testid="legal-status">
          <span className={`${styles.pill} ${saved?.published ? styles.pillOk : styles.pillIdle}`}>
            <span className={styles.pillDot} aria-hidden="true" />
            {saved?.published ? "Published" : "Draft"}
          </span>
          <span className={`${styles.pill} ${styles.pillIdle}`}>Version {saved?.version ?? 0}</span>
          {previewUrl ? (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className={styles.secondaryBtn}>
              Preview <MdOpenInNew aria-hidden="true" />
            </a>
          ) : (
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled
              title={isUrlMode ? "Enter a valid https:// URL first" : "No website address configured for this tenant"}
            >
              Preview <MdOpenInNew aria-hidden="true" />
            </button>
          )}
        </div>
      </header>
      {updatedAt && (
        <p className={styles.fieldHelp}>
          Last updated {updatedAt}
          {saved?.updated_by != null ? ` by user #${saved.updated_by}` : ""}
        </p>
      )}

      {!canEdit && <p className={styles.readOnlyNote}>You have view-only access to Settings.</p>}

      <fieldset className={styles.intGroup}>
        <legend className={styles.intGroupTitle}>Source</legend>
        <div className={styles.legalModes} role="radiogroup" aria-label="Source">
          {[
            [LEGAL_MODES.CONTENT, "Write the page here"],
            [LEGAL_MODES.URL, "Link to an external page"],
          ].map(([mode, label]) => (
            <label key={mode} className={styles.legalMode}>
              <input
                type="radio"
                name={id("mode")}
                value={mode}
                checked={form.mode === mode}
                disabled={!canEdit}
                onChange={() => setField(type, "mode", mode)}
              />
              {label}
            </label>
          ))}
        </div>
        {errorFor("mode")}
      </fieldset>

      <fieldset className={styles.intGroup}>
        <legend className={styles.intGroupTitle}>Page</legend>
        <div className={styles.intGrid}>
          <div className={styles.intField}>
            <div className={styles.intLabelRow}>
              <label htmlFor={id("title")} className={styles.intLabel}>Title</label>
            </div>
            <input
              type="text"
              maxLength={TITLE_MAX + 20}
              value={form.title}
              onChange={(e) => setField(type, "title", e.target.value)}
              className={`${styles.intInput} ${fieldErrors.title ? styles.inputError : ""}`}
              {...inputProps("title")}
            />
            <div className={styles.intMeta}>{errorFor("title")}</div>
          </div>

          {!isUrlMode && (
            <div className={styles.intField}>
              <div className={styles.intLabelRow}>
                <label htmlFor={id("effective_date")} className={styles.intLabel}>Effective date</label>
              </div>
              <input
                type="date"
                value={form.effective_date || ""}
                onChange={(e) => setField(type, "effective_date", e.target.value)}
                className={`${styles.intInput} ${fieldErrors.effective_date ? styles.inputError : ""}`}
                {...inputProps("effective_date")}
              />
              <div className={styles.intMeta}>{errorFor("effective_date")}</div>
            </div>
          )}

          {isUrlMode && (
            <div className={`${styles.intField} ${styles.intFieldWide}`}>
              <div className={styles.intLabelRow}>
                <label htmlFor={id("external_url")} className={styles.intLabel}>External page URL</label>
              </div>
              <div className={styles.intSendRow}>
                <input
                  type="url"
                  spellCheck={false}
                  placeholder="https://example.com/terms"
                  value={form.external_url}
                  onChange={(e) => setField(type, "external_url", e.target.value)}
                  className={`${styles.intInput} ${fieldErrors.external_url ? styles.inputError : ""}`}
                  {...inputProps("external_url")}
                />
                {isHttpsUrl(trimmedUrl) ? (
                  <a href={trimmedUrl} target="_blank" rel="noopener noreferrer" className={styles.secondaryBtn}>
                    Open <MdOpenInNew aria-hidden="true" />
                  </a>
                ) : (
                  <span className={styles.secondaryBtn} aria-disabled="true" style={{ opacity: 0.5 }}>
                    Open <MdOpenInNew aria-hidden="true" />
                  </span>
                )}
              </div>
              <div className={styles.intMeta}>{errorFor("external_url")}</div>
            </div>
          )}
        </div>

        {!isUrlMode && (
          <div className={styles.legalEditor}>
            <div>
              <div className={styles.intLabelRow}>
                <label htmlFor={id("content_html")} className={styles.intLabel}>Content (HTML)</label>
              </div>
              {canEdit && (
                <div className={styles.legalToolbar} role="toolbar" aria-label="Formatting">
                  {EDITOR_TOOLS.map((tool) => (
                    <button key={tool.key} type="button" className={styles.ghostBtn} title={tool.title} onClick={() => applyTool(tool)}>
                      {tool.label}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                ref={editorRef}
                rows={16}
                spellCheck
                value={form.content_html}
                onChange={(e) => setField(type, "content_html", e.target.value)}
                className={`${styles.intInput} ${styles.legalTextarea} ${fieldErrors.content_html ? styles.inputError : ""}`}
                {...inputProps("content_html")}
              />
              <div className={styles.intMeta}>
                {errorFor("content_html") || (
                  <span className={styles.fieldHelp}>
                    Headings, paragraphs, lists, links, tables, div/span and inline styles. Scripts, images, SVG and
                    unsafe CSS are removed when saved -- the preview shows the saved result.
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className={styles.intLabelRow}>
                <span className={styles.intLabel}>Live preview</span>
              </div>
              <iframe title={`${section.title} preview`} sandbox="" srcDoc={previewDoc(previews[type] ?? form.content_html)} className={styles.legalPreview} />
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className={styles.intGroup}>
        <legend className={styles.intGroupTitle}>Visibility</legend>
        <div className={styles.header}>
          <label htmlFor={id("published")} className={styles.intLabel}>
            Published {form.published ? "(visible on the website)" : "(hidden from the website)"}
          </label>
          <label className={styles.switch}>
            <input
              id={id("published")}
              type="checkbox"
              aria-label="Published"
              checked={form.published}
              disabled={!canEdit}
              onChange={(e) => setField(type, "published", e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        </div>
        {errorFor("published")}
      </fieldset>

      {canEdit && (
        <div className={`${styles.intFooter} ${dirty ? styles.intFooterDirty : ""}`}>
          <span className={styles.fieldHelp}>{dirty ? "You have unsaved changes" : "All changes saved."}</span>
          <div className={styles.intFooterActions}>
            {dirty && (
              <button type="button" className={styles.ghostBtn} onClick={handleDiscard} disabled={saving === type}>
                Discard
              </button>
            )}
            <button type="button" className={styles.primaryBtn} onClick={handleSave} disabled={saving === type || !dirty}>
              {saving === type ? "Saving…" : `Save ${section.title}`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default LegalSettings;
